import { ProductIdentifier } from '@/modules/product/types'
import { n, t } from '@/plugins/i18n.ts'
import router from '@/router'
import { SelectOption, ViewToggle } from '@/types'
import {
  Availability,
  BundleItemProduct,
  FullProduct,
  Money,
  ProductSearchView,
  SimplePrice,
  StorefrontProductSort
} from '@/types/index'
import { mdiViewList, mdiViewModule, mdiViewSplitVertical } from '@mdi/js'
import { Ref, computed } from 'vue'
import { RouteLocationNormalizedLoaded } from 'vue-router'

const formatMoney = (money?: Money | null, isResellPrice = false): string => {
  if (money && money.value != 0) {
    return formatMoneyShowZeroPrice(money, isResellPrice)
  } else {
    return isResellPrice ? t('price.priceOnRequest').toString() : t('nullPriceLabel').toString()
  }
}

const formatMoneyShowZeroPrice = (money?: Money | null, isResellPrice = false): string => {
  money ??= { value: 0, currency: 'EUR' }
  return isResellPrice
    ? n(money.value, { key: 'currency', currency: money.currency }) + t('price.priceVatStar')
    : n(money.value, { key: 'currency', currency: money.currency })
}

const formatPurchasePrice = (price: number, currency: string): string => {
  if (price > 0) {
    return n(price, { key: 'currency', currency })
  } else {
    return t('nullPriceLabel').toString()
  }
}

const formatDiscount = (price1: number, price2: number): string => {
  return n(price2 / price1 - 1, 'percentage')
}

const getStockStatusColor = (status?: Availability): string => {
  switch (status) {
    case Availability.ON_STOCK:
    case Availability.AVAILABLE:
      return 'green-darken-2'
    case Availability.ORDERED:
    case Availability.WAREHOUSE:
    case Availability.ON_ORDER:
      return 'orange-darken-2'
    case Availability.NOT_AVAILABLE:
    case Availability.UNKNOWN:
      return 'red'
    default:
      return 'grey'
  }
}

const getProductIdentifiers = (
  product: FullProduct | BundleItemProduct,
  showProductIds: boolean
): ProductIdentifier[] => {
  const identifiers: ProductIdentifier[] = []
  if (product) {
    if (product.articleNumber.additionalId) {
      identifiers.push({
        name: t('additionalId'),
        value: product.articleNumber.additionalId
      })
    }
    if (showProductIds) {
      if (product.articleNumber.manufacturerProductId) {
        identifiers.push({
          name: t('manufacturerId'),
          value: product.articleNumber.manufacturerProductId
        })
      }
      if (product.articleNumber.ean) {
        identifiers.push({
          name: t('ean'),
          value: product.articleNumber.ean
        })
      }
      if (product.productId) {
        identifiers.push({
          name: t('productId'),
          value: product.productId.toString()
        })
      }
    }
  }
  return identifiers
}

const getProductSortOptions = (): SelectOption<StorefrontProductSort>[] => {
  return Object.values(StorefrontProductSort).map((option) => ({
    title: mapProductSortToText(option),
    value: option
  }))
}

const mapProductSortToText = (sort: StorefrontProductSort): string =>
  t('productSort.' + sort).toString()

const getProductViews = (showSuppliers: boolean): ViewToggle<ProductSearchView>[] => [
  {
    name: ProductSearchView.CARDS,
    altText: t('viewToggle.productsCards'),
    icon: mdiViewModule,
    restriction: () => true,
    showOnMobile: false
  },
  {
    name: ProductSearchView.LIST,
    altText: t('viewToggle.productsList'),
    icon: showSuppliers ? mdiViewSplitVertical : mdiViewList,
    restriction: () => true,
    showOnMobile: false
  }
]

const useProductRouterQuery = (
  route: RouteLocationNormalizedLoaded
): {
  keyword: Ref<string | undefined>
  sort: Ref<StorefrontProductSort | undefined>
  filters: Ref<string[]>
} => {
  const keyword = computed(() => route.query.keyword as string | undefined)
  const sort = computed(() => {
    const sortIdentifier = route.query.sort as string | undefined
    return Object.values(StorefrontProductSort).find((s) => s == sortIdentifier)
  })
  const filters = computed(() => {
    const filterTerm = route.query.filter as string | undefined
    if (!filterTerm) {
      return []
    }
    return filterTerm.split(',')
  })

  return { keyword, sort, filters }
}

const getProductLink = (productId: number) => {
  return router.resolve({ name: 'Product', params: { id: productId } }).href
}

const isFullProduct = (product: unknown): product is FullProduct => {
  return (product as FullProduct).productId != undefined
}

const canBeRented = (price?: SimplePrice): boolean => {
  return !!(price && price.canBeRented && price.resellPrice.value > 0)
}

export {
  canBeRented,
  formatDiscount,
  formatMoney,
  formatMoneyShowZeroPrice,
  formatPurchasePrice,
  getProductIdentifiers,
  getProductLink,
  getProductSortOptions,
  getProductViews,
  getStockStatusColor,
  isFullProduct,
  mapProductSortToText,
  useProductRouterQuery
}
